import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Switch,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { ChevronLeft, Pencil, Check, X, Lock, Building2, Users, Info } from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import PlatformCompanyKeys from './PlatformCompanyKeys';
import PartnerCompaniesTab from './PartnerCompaniesTab';
import PartnerUsersTab from './PartnerUsersTab';
import PartnerEntitlementsTab from './PartnerEntitlementsTab';
import AuditPanel from './AuditPanel';
import UsageAnalyticsPanel from '../shared/UsageAnalytics/UsageAnalyticsPanel';
import { normalizeCredentials } from '../shared/LLMKeys/llmKeyStatus';
import { describeApiError, apiErrorText } from '../../utils/apiError';
import { companyLabel } from '../../utils/tenancy';
import { PLATFORM_PARTNER_COPY, TENANCY_COPY, USAGE_COPY } from '../../constants/partnerCopy';
import styles from './PlatformAdmin.module.scss';

const HIDE_PARTNER_DIMENSION = ['partner'];
const COMPANY_MODEL = ['company', 'model'];

/**
 * A partner's detail page on Platform Admin (Partner tier guide §4.3): header
 * (rename, status, counts) + Companies · Admins & users · Feature limits ·
 * LLM keys · Usage · Audit log.
 */
const PartnerDetail = ({ partner: initialPartner, catalog, partnerIndex, notify, onBack, onChanged }) => {
  const [partner, setPartner] = useState(initialPartner);
  const [tab, setTab] = useState('companies');

  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(initialPartner?.name || '');
  const [nameError, setNameError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const partnerId = partner?.id;
  const homeId = partner?.home_company_id;

  const refresh = useCallback(async () => {
    if (!partnerId) return;
    try {
      const data = await platformAdminService.getPartner(partnerId);
      setPartner((prev) => ({ ...prev, ...(data?.partner || data) }));
    } catch (err) {
      // Keep the list row we were opened with; the page still works from it.
      console.warn('Failed to refresh partner', err);
    }
  }, [partnerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const afterChange = useCallback(async () => {
    await refresh();
    await onChanged?.();
  }, [refresh, onChanged]);

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name || name === partner.name) {
      setRenaming(false);
      return;
    }
    try {
      setSaving(true);
      setNameError(null);
      await platformAdminService.updatePartner(partnerId, { name });
      notify('Partner renamed.', 'success');
      setRenaming(false);
      await afterChange();
    } catch (err) {
      if (err?.status === 400 && /already exists/i.test(apiErrorText(err))) {
        setNameError('A partner with this name already exists.');
      } else {
        notify(describeApiError(err, 'Failed to rename partner.'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (isActive) => {
    try {
      setSaving(true);
      await platformAdminService.updatePartner(partnerId, { is_active: isActive });
      notify(
        isActive
          ? 'Partner reactivated. Everyone under it can sign in again.'
          : 'Partner deactivated. Everyone under it is blocked until you reactivate it.',
        isActive ? 'success' : 'info'
      );
      setConfirmDeactivate(false);
      await afterChange();
    } catch (err) {
      notify(describeApiError(err, 'Failed to update partner status.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Shared by the Usage and Audit tabs: the partner's client companies plus its
  // staff ("Direct users") as a pseudo-company.
  const loadCompanyOptions = useCallback(async () => {
    const data = await platformAdminService.listPartnerCompanies(partnerId, { limit: 200 });
    const list = data?.companies || (Array.isArray(data) ? data : []);
    return homeId != null ? [{ id: homeId, name: TENANCY_COPY.directUsers }, ...list] : list;
  }, [partnerId, homeId]);

  const companyLabelFn = useCallback(
    (id, name) => companyLabel(id, name, { homeCompanyId: homeId }),
    [homeId]
  );

  const usageFixedParams = useMemo(() => ({ partner_id: partnerId }), [partnerId]);

  if (!partner) return null;

  return (
    <>
      <Box className={styles.detailHeader}>
        <Button onClick={onBack} startIcon={<ChevronLeft size={18} />} className={styles.backBtn}>
          All partners
        </Button>
        <Box className={styles.detailTitleRow} sx={{ flexWrap: 'wrap' }}>
          {renaming ? (
            <>
              <TextField
                size="small"
                autoFocus
                value={nameDraft}
                onChange={(e) => { setNameDraft(e.target.value); setNameError(null); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') setRenaming(false);
                }}
                error={!!nameError}
                helperText={nameError}
                disabled={saving}
                sx={{ minWidth: 260 }}
              />
              <IconButton size="small" className={styles.subtleBtn} onClick={saveName} disabled={saving} title="Save name">
                {saving ? <CircularProgress size={16} color="inherit" /> : <Check size={16} />}
              </IconButton>
              <IconButton
                size="small"
                className={styles.subtleBtn}
                onClick={() => { setRenaming(false); setNameDraft(partner.name); setNameError(null); }}
                disabled={saving}
                title="Cancel"
              >
                <X size={16} />
              </IconButton>
            </>
          ) : (
            <>
              <Typography variant="h5" className={styles.detailTitle}>{partner.name}</Typography>
              <IconButton
                size="small"
                className={styles.subtleBtn}
                onClick={() => { setNameDraft(partner.name); setRenaming(true); }}
                title="Rename partner"
              >
                <Pencil size={15} />
              </IconButton>
            </>
          )}
          <span className={`${styles.statusPill} ${partner.is_active ? styles.active : styles.inactive}`}>
            {partner.is_active ? 'Active' : TENANCY_COPY.deactivated}
          </span>
          <Tooltip title={PLATFORM_PARTNER_COPY.statusTooltip} arrow>
            <span>
              <Switch
                size="small"
                checked={!!partner.is_active}
                disabled={saving}
                onChange={(e) => (e.target.checked ? setActive(true) : setConfirmDeactivate(true))}
              />
            </span>
          </Tooltip>
          <span className={styles.badge}>
            <Building2 size={12} />
            {partner.company_count ?? 0} companies
          </span>
          <span className={styles.badge}>
            <Users size={12} />
            {partner.user_count ?? 0} users
          </span>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        className={styles.tabs}
        TabIndicatorProps={{ style: { backgroundColor: '#e36250' } }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab value="companies" label="Companies" className={styles.tab} />
        <Tab value="users" label="Admins & users" className={styles.tab} />
        <Tab value="limits" label="Feature limits" className={styles.tab} />
        <Tab value="keys" label="LLM keys" className={styles.tab} />
        <Tab value="usage" label="Usage" className={styles.tab} />
        <Tab value="audit" label="Audit log" className={styles.tab} />
      </Tabs>

      {tab === 'companies' && (
        <PartnerCompaniesTab partner={partner} partnerIndex={partnerIndex} notify={notify} onChanged={afterChange} />
      )}
      {tab === 'users' && <PartnerUsersTab partner={partner} notify={notify} onChanged={afterChange} />}
      {tab === 'limits' && <PartnerEntitlementsTab partner={partner} catalog={catalog} notify={notify} />}
      {tab === 'keys' && <PartnerKeysTab partner={partner} notify={notify} />}
      {tab === 'usage' && (
        <UsageAnalyticsPanel
          basePath="/platform/usage"
          scope="platform"
          notify={notify}
          fixedParams={usageFixedParams}
          defaultGroupBy={COMPANY_MODEL}
          hiddenDimensions={HIDE_PARTNER_DIMENSION}
          loadCompanyOptions={loadCompanyOptions}
          companyLabelFn={companyLabelFn}
          dateTooltip={USAGE_COPY.attribution}
        />
      )}
      {tab === 'audit' && (
        <AuditPanel
          notify={notify}
          fixedPartnerId={partnerId}
          loadCompanyOptions={loadCompanyOptions}
          companyLabelFn={companyLabelFn}
        />
      )}

      {/* Deactivate confirmation (§4.3) */}
      <Dialog
        open={confirmDeactivate}
        onClose={() => !saving && setConfirmDeactivate(false)}
        classes={{ paper: styles.dialogPaper }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={styles.dialogTitle}>{PLATFORM_PARTNER_COPY.deactivateTitle(partner.name)}</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteText}>
            {PLATFORM_PARTNER_COPY.deactivateBody(partner.company_count ?? 0)}
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirmDeactivate(false)} disabled={saving} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => setActive(false)}
            disabled={saving}
            className={styles.dangerButton}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {PLATFORM_PARTNER_COPY.deactivateConfirm}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * The partner's own keys = its home company's credential rows (§4.6). The
 * fallback switch here decides whether the whole partner tree may use
 * platform-funded keys.
 */
const PartnerKeysTab = ({ partner, notify }) => {
  const [providers, setProviders] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const homeId = partner.home_company_id;

  const loadCredentials = useCallback(async () => {
    try {
      const data = await platformAdminService.getCompanyLLMKeys(homeId);
      setCredentials(normalizeCredentials(data));
    } catch (err) {
      notify(describeApiError(err, "Failed to load the partner's keys."), 'error');
    }
  }, [homeId, notify]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [providerData] = await Promise.all([
          platformAdminService.getProviders(true),
          loadCredentials(),
        ]);
        if (!cancelled) {
          setProviders(Array.isArray(providerData) ? providerData : providerData?.providers || []);
        }
      } catch (err) {
        if (!cancelled) notify(describeApiError(err, 'Failed to load providers.'), 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCredentials, notify]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box className={styles.noteBanner}>
        <Info size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          {`These are ${partner.name}'s own keys. Its direct users use them, and its client companies inherit them unless inheritance is turned off per provider. "Partner may fall back to platform keys" decides whether the partner and all its companies can use the platform's keys when it has none.`}
        </span>
      </Box>
      <PlatformCompanyKeys
        companyId={homeId}
        companyName={partner.name}
        variant="partnerHome"
        partnerName={partner.name}
        providers={providers}
        credentials={credentials}
        onChanged={loadCredentials}
        notify={notify}
      />
    </Box>
  );
};

export default PartnerDetail;
